// US lexicon: maps AU-English UI strings to their American equivalents.
// t() is an identity function for any non-US country, so AU (and any
// unknown/missing country) rendering is unchanged by construction.
// Keys must be the EXACT AU string as it appears at the call site; only
// apply t() to display strings, never to values that are persisted or
// compared (e.g. submission.year payloads, option-matching titles).

const US_TERMS = {
  'Marking Tools': 'Grading Tools',
  'Marking Template': 'Grading Template',
  'Marking Template:': 'Grading Template:',
  'Marking Criteria': 'Grading Criteria',
  'Marking criteria': 'Grading criteria',
  'Short Answer Marking Criteria': 'Short Answer Grading Criteria',
  'Marking Method : ': 'Grading Method : ',
  'Your marking preferences': 'Your grading preferences',
  'Marking Preferences': 'Grading Preferences',
  'Set your marking preferences to help JeddAI tailor feedback to your style':
    'Set your grading preferences to help JeddAI tailor feedback to your style',
  'Get Feedback - Select Marking Preferences':
    'Get Feedback - Select Grading Preferences',
  'Mark Now': 'Grade Now',
  'Mark saved': 'Score saved',
  'Unable to save mark': 'Unable to save score',
  "This response can only be marked by one teacher. By clicking 'Mark Now' you will become the sole marker for this submission. Other teachers will still be able to view the response, but only you can provide feedback.":
    "This response can only be graded by one teacher. By clicking 'Grade Now' you will become the sole grader for this submission. Other teachers will still be able to view the response, but only you can provide feedback.",
  'Marking preferences are only available to school or teacher plan users.':
    'Grading preferences are only available to school or teacher plan users.',
  'These marking preferences apply to all your JeddAI feedback. Per-class overrides unlock when your school adds you to classes.':
    'These grading preferences apply to all your JeddAI feedback. Per-class overrides unlock when your school adds you to classes.',
  'Use custom marking preferences for this class.':
    'Use custom grading preferences for this class.',
  'Defaults source: your global marking preferences.':
    'Defaults source: your global grading preferences.',
  'Use task-level marking preferences to override class and global settings for this task.':
    'Use task-level grading preferences to override class and global settings for this task.',
  'Override your marking preferences for this task.':
    'Override your grading preferences for this task.',
  'Enable override to apply task-level marking preferences across all selected classes.':
    'Enable override to apply task-level grading preferences across all selected classes.',
  Mark: 'Score',
  Marks: 'Points',
  'Marks (optional)': 'Points (optional)',
  'Marks out of': 'Points out of',
  'Highest Marks': 'Highest Score',
  'Use mark ranges': 'Use score ranges',
  'Add mark range': 'Add score range',
  'Remove mark range': 'Remove score range',
  'Marking guides with expected answers and point allocations for short response questions.':
    'Grading guides with expected answers and point allocations for short response questions.',
  'Tip: Short Answer Marking Criteria is the recommended feedback type for Short Answers.':
    'Tip: Short Answer Grading Criteria is the recommended feedback type for Short Answers.',
  'Show marks for this question': 'Show points for this question',
  'Set a maximum mark for this question.':
    'Set a maximum score for this question.',
  'Customise your marking preferences': 'Customize your grading preferences',
  "Let's Get Started - Customise Your Feedback":
    "Let's Get Started - Customize Your Feedback",
  'Year and location successfully updated':
    'Grade and location successfully updated',
  'Learn more about Short Answer Marking Criteria':
    'Learn more about Short Answer Grading Criteria',
  Year: 'Grade',
  'Year level(s)': 'Grade level(s)',
  'Pick the year level(s) and subject(s) for each class so marking and feedback are aligned to the right curriculum.':
    'Pick the grade level(s) and subject(s) for each class so grading and feedback are aligned to the right standards.',
  "Your learning platform doesn't tell Jeddle what each class teaches. Pick the year level(s) and subject(s) for each class so marking and feedback are aligned to the right curriculum.":
    "Your learning platform doesn't tell Jeddle what each class teaches. Pick the grade level(s) and subject(s) for each class so grading and feedback are aligned to the right standards.",
  Favourite: 'Favorite',
  Favourites: 'Favorites',
  'Add to favourites': 'Add to favorites',
  'Pick focus area colour*': 'Pick focus area color*',
  'Selected colour': 'Selected color',
  'Pick a colour for focus area': 'Pick a color for focus area',
};

export const isUsCountry = (country) => {
  const c = String(country || '')
    .trim()
    .toUpperCase();
  return c === 'US' || c === 'USA' || c === 'UNITED STATES';
};

// New Zealand (NCEA) detection. Mirrors backend Region.NZ canonicalization.
// NZ is NEVER US (isUsCountry stays false for NZ), so no US_TERMS/US_PHRASE_RULES
// ever apply — NZ already uses British/AU spelling and "Marking"/"Year N" chrome.
export const isNzCountry = (country) => {
  const c = String(country || '')
    .trim()
    .toUpperCase();
  return c === 'NZ' || c === 'NZL' || c === 'NEW ZEALAND';
};

// Last-resort curriculum-state default when a user/class exposes no explicit
// state. NZ resolves to its single "NZ" sentinel; every other country keeps the
// historical AU "NSW" default. This exists so a null-state NZ context never
// resolves under NSW (which classifies AU and would hide NZ tools).
export const regionDefaultState = (country) =>
  isNzCountry(country) ? 'NZ' : 'NSW';

export const t = (text, country) =>
  (isUsCountry(country) && US_TERMS[text]) || text;

// Phrase-level localizer for CONTENT surfaces (tooltips, tour copy, plan
// descriptions) where enumerating exact strings is impractical. Word-boundary
// replacements only; mark→grade is included because opt-in surfaces are prose
// (never the numeric Marks UI, which uses exact-key t()). Identity for non-US.
const US_PHRASE_RULES = [
  [/\bMarking\b/g, 'Grading'],
  [/\bmarking\b/g, 'grading'],
  [/\bMarked\b/g, 'Graded'],
  [/\bmarked\b/g, 'graded'],
  [/\bMarker\b/g, 'Grader'],
  [/\bmarker\b/g, 'grader'],
  [/\bMark\b/g, 'Grade'],
  [/\bmark\b/g, 'grade'],
  [/Customis/g, 'Customiz'],
  [/customis/g, 'customiz'],
  [/Personalis/g, 'Personaliz'],
  [/personalis/g, 'personaliz'],
  [/Organis/g, 'Organiz'],
  [/organis/g, 'organiz'],
  [/Analyse/g, 'Analyze'],
  [/analyse/g, 'analyze'],
  [/Analysing/g, 'Analyzing'],
  [/analysing/g, 'analyzing'],
  [/Recognis/g, 'Recogniz'],
  [/recognis/g, 'recogniz'],
  [/Summaris/g, 'Summariz'],
  [/summaris/g, 'summariz'],
  [/Finalis/g, 'Finaliz'],
  [/finalis/g, 'finaliz'],
  [/Prioritis/g, 'Prioritiz'],
  [/prioritis/g, 'prioritiz'],
  [/\bPractise\b/g, 'Practice'],
  [/\bpractise\b/g, 'practice'],
  [/Colour/g, 'Color'],
  [/colour/g, 'color'],
  [/Favourite/g, 'Favorite'],
  [/favourite/g, 'favorite'],
  [/Behaviour/g, 'Behavior'],
  [/behaviour/g, 'behavior'],
  [/Enrolment/g, 'Enrollment'],
  [/enrolment/g, 'enrollment'],
  [/\bCentre\b/g, 'Center'],
  [/\bcentre\b/g, 'center'],
];

export const localizeText = (text, country) => {
  if (!isUsCountry(country) || typeof text !== 'string') return text;
  return US_PHRASE_RULES.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    text
  );
};

// Display label for a school year/grade. Accepts 7, '7', 'Year 7', 'Grade 7'.
export const yearLabel = (year, country) => {
  const n = String(year ?? '')
    .replace(/^(Year|Grade)-?\s*/i, '')
    .trim();
  return isUsCountry(country) ? `Grade ${n}` : `Year ${n}`;
};
